/**
 * Crew (configurations) API: a named line-up, one roster sailor per seat.
 * A CREW interval's crew_code is the name of the crew its line-up matches.
 */
import { apiEndpoints } from '@config/env';
import { deleteData, getData, postData, putData } from '../utils/global';
import {
  CREW_SEAT_COLUMNS,
  CREW_SEATS,
  planNewCrewConfig,
  type CrewDefinition,
  type CrewRosterSailor,
} from '../utils/crewCode';

export type CrewRow = CrewDefinition & {
  notes: string;
  active: boolean;
  date_created?: string;
  date_modified?: string;
};

export type CrewSeatIds = Pick<CrewDefinition, (typeof CREW_SEAT_COLUMNS)[keyof typeof CREW_SEAT_COLUMNS]>;

export type CrewWriteResult =
  | {
      ok: true;
      row: CrewRow;
      message: string;
      eventsRenamed: number;
      /** Events still carrying a previous code. Zero when this save rewrote them. */
      eventsPending: number;
      /** Previous codes those events still use. Empty after they are rewritten. */
      previousNames: string[];
    }
  | { ok: false; message: string };

function seatId(value: unknown): number | null {
  if (value == null || value === '') {return null;}
  const n = Number(value);
  return Number.isInteger(n) ? n : null;
}

function normalizeRow(raw: Record<string, unknown>): CrewRow {
  const row = {
    crew_id: Number(raw.crew_id),
    name: String(raw.name ?? '').trim(),
    notes: String(raw.notes ?? ''),
    active: raw.active !== false,
    date_created: raw.date_created as string | undefined,
    date_modified: raw.date_modified as string | undefined,
  } as CrewRow;
  for (const seat of CREW_SEATS) {
    const col = CREW_SEAT_COLUMNS[seat];
    row[col] = seatId(raw[col]);
  }
  return row;
}

function query(className: string, projectId: number): string {
  return `class_name=${encodeURIComponent(className.toLowerCase())}&project_id=${projectId}`;
}

/** null when the crews could not be read -- not the same as none defined. */
export async function fetchCrews(className: string, projectId: number): Promise<CrewRow[] | null> {
  const res = await getData(`${apiEndpoints.app.crew}?${query(className, projectId)}`, undefined, { bestEffort: true });
  if (!res?.success || !Array.isArray(res.data)) {return null;}
  return (res.data as Array<Record<string, unknown>>).map(normalizeRow);
}

function writeResult(res: { success?: boolean; data?: unknown; message?: string } | null | undefined): CrewWriteResult {
  if (res?.success && res.data && typeof res.data === 'object') {
    const data = res.data as Record<string, unknown>;
    return {
      ok: true,
      row: normalizeRow(data),
      message: res.message || '',
      eventsRenamed: Number(data.events_renamed ?? 0) || 0,
      eventsPending: Number(data.events_pending ?? 0) || 0,
      previousNames: Array.isArray(data.previous_names)
        ? data.previous_names.map((name) => String(name)).filter((name) => name.trim() !== '')
        : [],
    };
  }
  return { ok: false, message: res?.message || 'Request failed' };
}

export async function createCrew(
  className: string,
  projectId: number,
  crew: CrewSeatIds & { name: string; notes?: string; active?: boolean }
): Promise<CrewWriteResult> {
  const res = await postData(apiEndpoints.app.crew, {
    class_name: className.toLowerCase(),
    project_id: projectId,
    ...crew,
  });
  return writeResult(res);
}

export type CrewUpdatePatch = Partial<CrewSeatIds & Pick<CrewRow, 'name' | 'notes' | 'active'>> & {
  /**
   * When false, a rename leaves event tags on the previous code.
   * Omitted or true rewrites `crew_code` / `CREW_CODE` on events that still use `previous_names`.
   */
  update_events?: boolean;
  /** Codes already on events from earlier renames that were not written through. */
  previous_names?: string[];
};

export async function updateCrew(
  className: string,
  projectId: number,
  crewId: number,
  patch: CrewUpdatePatch
): Promise<CrewWriteResult> {
  const res = await putData(`${apiEndpoints.app.crew}/${crewId}`, {
    class_name: className.toLowerCase(),
    project_id: projectId,
    ...patch,
  });
  return writeResult(res);
}

export async function deleteCrew(
  className: string,
  projectId: number,
  crewId: number
): Promise<{ ok: true } | { ok: false; message: string }> {
  const res = await deleteData(`${apiEndpoints.app.crew}/${crewId}?${query(className, projectId)}`, {});
  return res?.success ? { ok: true } : { ok: false, message: res?.message || 'Remove failed' };
}

export type EnsureCrewResult =
  | { ok: true; crews: CrewRow[]; created: CrewRow | null }
  | { ok: false; message: string };

function isCrewLineupConflict(message: string): boolean {
  const text = message.toLowerCase();
  return text.includes('these seats') || text.includes('lineup') || text.includes('line-up');
}

function isCrewNameConflict(message: string): boolean {
  const text = message.toLowerCase();
  return text.includes('already called') || text.includes('that name');
}

/**
 * Insert a crew when `tags` is a roster line-up with no config yet.
 * The name is the first free helm / wing / flight initial combination.
 * A lineup that already exists is returned as `created: null` with the fresh list.
 */
export async function ensureCrewConfigForLineup(
  className: string,
  projectId: number,
  tags: Record<string, unknown>,
  roster: readonly CrewRosterSailor[],
  crews: readonly CrewRow[]
): Promise<EnsureCrewResult> {
  let latest: readonly CrewRow[] = crews;
  const reserved: string[] = [];
  for (let attempt = 0; attempt < 12; attempt += 1) {
    const plan = planNewCrewConfig(tags, roster, latest, reserved);
    if (!plan) {return { ok: true, crews: latest as CrewRow[], created: null };}
    const res = await createCrew(className, projectId, { name: plan.name, ...plan.seats });
    if (res.ok) {
      const without = latest.filter((crew) => crew.crew_id !== res.row.crew_id);
      return { ok: true, crews: [...without, res.row], created: res.row };
    }
    if (!isCrewLineupConflict(res.message) && !isCrewNameConflict(res.message)) {
      return { ok: false, message: res.message };
    }
    if (isCrewNameConflict(res.message)) {reserved.push(plan.name);}
    const fresh = await fetchCrews(className, projectId);
    if (fresh) {latest = fresh;}
    if (isCrewLineupConflict(res.message)) {
      if (planNewCrewConfig(tags, roster, latest, reserved) == null) {
        return { ok: true, crews: latest as CrewRow[], created: null };
      }
      return { ok: false, message: res.message };
    }
  }
  return { ok: false, message: 'Could not add a crew code for this line-up' };
}
